import { useCallback } from "react";
import {
  useCreateVendorListMutation,
  useUpdateVendorListMutation,
  useDuplicateVendorListMutation,
  useAddVendorListMembersMutation,
  useDeleteVendorListMutation,
  useRemoveVendorListMemberMutation,
  useReorderVendorListMembersMutation,
  useReorderSystemListMembersMutation,
  useAddManualMyVendorMutation,
} from "./queries";
import { mapVendorListMutationResponse } from "../utils/myVendorListsMapper";
import { exportVendorListAsCsv, exportVendorListAsExcel } from "../utils/exportVendorList";
import {
  hydrateVendorListMemberPayloads,
  resolveVendorProviderId,
  isValidProviderId,
} from "../utils/myVendorListsUtils";
import { resolveVendorListTableRows } from "../utils/myVendorListViewUtils";
import { isSystemListId } from "../utils/myVendorListsConstants";
import { getProviderBasicProfile } from "../api/api-methods";
import { broadcastMyVendorsChanged } from "../utils/myVendorMembershipBroadcast";

export function useMyVendorListActions(sponsorId, { myVendors = [], vendorTagsMap = {} } = {}) {
  const createMutation = useCreateVendorListMutation(sponsorId);
  const updateMutation = useUpdateVendorListMutation(sponsorId);
  const duplicateMutation = useDuplicateVendorListMutation(sponsorId);
  const addMembersMutation = useAddVendorListMembersMutation(sponsorId);
  const deleteMutation = useDeleteVendorListMutation(sponsorId);
  const removeMemberMutation = useRemoveVendorListMemberMutation(sponsorId);
  const reorderMembersMutation = useReorderVendorListMembersMutation(sponsorId);
  const reorderSystemListMutation = useReorderSystemListMembersMutation(sponsorId);
  const addManualVendorMutation = useAddManualMyVendorMutation(sponsorId);

  /** Custom lists join live My Vendors rows — ensure members are in the manual set. */
  const ensureMyVendorsMembership = useCallback(
    async (vendors = []) => {
      const providerIds = [
        ...new Set(
          vendors
            .map((vendor) => resolveVendorProviderId(vendor))
            .filter((id) => isValidProviderId(id))
        ),
      ];
      if (!providerIds.length) return;
      await Promise.all(
        providerIds.map((providerId) => addManualVendorMutation.mutateAsync(providerId))
      );
      broadcastMyVendorsChanged(sponsorId);
    },
    [addManualVendorMutation, sponsorId]
  );

  const createList = useCallback(
    async ({ name, notes = "", vendors = [] }) => {
      const payload = {
        name,
        notes,
        vendors: await hydrateVendorListMemberPayloads(vendors, getProviderBasicProfile),
      };
      const result = await createMutation.mutateAsync(payload);
      if (vendors.length) {
        await ensureMyVendorsMembership(vendors);
      }
      return mapVendorListMutationResponse(result);
    },
    [createMutation, ensureMyVendorsMembership]
  );

  const renameList = useCallback(
    async (listId, name) => {
      if (isSystemListId(listId)) return null;
      const result = await updateMutation.mutateAsync({
        listId,
        payload: { name },
      });
      return mapVendorListMutationResponse(result);
    },
    [updateMutation]
  );

  const updateListNotes = useCallback(
    async (listId, notes) => {
      if (isSystemListId(listId)) return null;
      const result = await updateMutation.mutateAsync({
        listId,
        payload: { notes },
      });
      return mapVendorListMutationResponse(result);
    },
    [updateMutation]
  );

  const duplicateList = useCallback(
    async (listId) => {
      const result = await duplicateMutation.mutateAsync(listId);
      return mapVendorListMutationResponse(result);
    },
    [duplicateMutation]
  );

  const addMembersToList = useCallback(
    async (listId, vendors = []) => {
      if (isSystemListId(listId)) return null;
      const payload = {
        vendors: await hydrateVendorListMemberPayloads(vendors, getProviderBasicProfile),
      };
      if (!payload.vendors.length) return null;
      const result = await addMembersMutation.mutateAsync({ listId, payload });
      await ensureMyVendorsMembership(vendors);
      return mapVendorListMutationResponse(result);
    },
    [addMembersMutation, ensureMyVendorsMembership]
  );

  const deleteList = useCallback(
    async (listId) => {
      if (isSystemListId(listId)) return false;
      await deleteMutation.mutateAsync(listId);
      return true;
    },
    [deleteMutation]
  );

  const removeMemberFromList = useCallback(
    async (listId, providerId) => {
      if (isSystemListId(listId)) return null;
      const result = await removeMemberMutation.mutateAsync({ listId, providerId });
      return mapVendorListMutationResponse(result);
    },
    [removeMemberMutation]
  );

  const reorderListMembers = useCallback(
    async (listId, providerIds = []) => {
      if (isSystemListId(listId)) return null;
      const result = await reorderMembersMutation.mutateAsync({
        listId,
        payload: { provider_ids: providerIds },
      });
      return mapVendorListMutationResponse(result);
    },
    [reorderMembersMutation]
  );

  const reorderSystemListMembers = useCallback(
    async (systemListKind, providerIds = []) => {
      if (!systemListKind) return null;
      const result = await reorderSystemListMutation.mutateAsync({
        systemListKind,
        payload: { provider_ids: providerIds },
      });
      return result?.provider_ids || providerIds;
    },
    [reorderSystemListMutation]
  );

  const exportListAsCSV = useCallback((list) => {
    exportVendorListAsCsv(list, resolveVendorListTableRows(list, myVendors, vendorTagsMap));
  }, [myVendors, vendorTagsMap]);

  const exportListAsExcel = useCallback(async (list) => {
    await exportVendorListAsExcel(list, resolveVendorListTableRows(list, myVendors, vendorTagsMap));
  }, [myVendors, vendorTagsMap]);

  const isMutating =
    createMutation.isPending ||
    updateMutation.isPending ||
    duplicateMutation.isPending ||
    addMembersMutation.isPending ||
    deleteMutation.isPending ||
    removeMemberMutation.isPending ||
    reorderMembersMutation.isPending ||
    reorderSystemListMutation.isPending ||
    addManualVendorMutation.isPending;

  return {
    createList,
    renameList,
    updateListNotes,
    duplicateList,
    addMembersToList,
    deleteList,
    removeMemberFromList,
    reorderListMembers,
    reorderSystemListMembers,
    exportListAsCSV,
    exportListAsExcel,
    isMutating,
  };
}

export default useMyVendorListActions;
